import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { setAccessToken as setTokenManagerAccessToken, clearAccessToken as clearTokenManagerAccessToken } from '../api/tokenManager';
import { refreshAccessToken } from '../api/axios';
import { getRefreshToken, removeRefreshToken } from '../utils/authCookies';

interface User {
  username: string;
  authorities: string[];
}

interface AuthContextType {
  accessToken: string | null;
  user: User | null;
  isInitializing: boolean;
  login: (token: string, user: User, accessTokenExpiresAt?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionVersionRef = useRef(0);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const login = (token: string, userData: User, accessTokenExpiresAt?: string) => {
    setTokenManagerAccessToken(token, accessTokenExpiresAt);

    setAccessToken(token);
    setUser(userData);

    clearRefreshTimer();

    if (accessTokenExpiresAt) {
      const refreshInMs = Math.max(new Date(accessTokenExpiresAt).getTime() - Date.now() - 30000, 0);
      const sessionVersion = sessionVersionRef.current;

      refreshTimerRef.current = setTimeout(async () => {
        try {
          if (sessionVersion !== sessionVersionRef.current) return;

          if (!getRefreshToken()) {
            logout();
            return;
          }

          const response = await refreshAccessToken();

          if (!response) {
            logout();
            return;
          }

          if (sessionVersion !== sessionVersionRef.current) return;

          login(
            response.accessToken,
            {
              username: response.username,
              authorities: response.authorities,
            },
            response.accessTokenExpiresAt,
          );
        } catch (error) {
          logout();
        }
      }, refreshInMs);
    }
  };

  const logout = () => {
    sessionVersionRef.current += 1;
    clearRefreshTimer();
    clearTokenManagerAccessToken();
    removeRefreshToken();

    setAccessToken(null);
    setUser(null);
    setIsInitializing(false);
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const sessionVersion = sessionVersionRef.current;

      try {
        if (!getRefreshToken()) return;

        const response = await refreshAccessToken();

        if (!response || sessionVersion !== sessionVersionRef.current) return;

        login(
          response.accessToken,
          {
            username: response.username,
            authorities: response.authorities,
          },
          response.accessTokenExpiresAt,
        );
      } catch (error) {
        removeRefreshToken();
        logout();
      } finally {
        if (sessionVersion === sessionVersionRef.current) {
          setIsInitializing(false);
        }
      }
    };

    initializeAuth();

    return clearRefreshTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        user,
        isInitializing,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
};
